import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import AditionalInfo from "../AditionalInfo/AditionalInfo";
import { FilesList } from "../Files/FilesList";
import { useMobile } from "../Layout/UseResponsive";
import AssistanceProfitabilityTable from "../Profitability/AssistanceProfitabilityTable";
import { SelectItemsProps } from "../Select2Component";
import SelectComponent from "../SelectComponent";
import StatusHistoryTable from "../StatusHistory/StatusHistoryTable";
import useDynamicComponent from "../dynamics/useDynamicComponent";
import { AssistanceAdditionalInfoComponent } from "./AssistanceAdditionalInfoComponent";
import AssistanceCheckListComponent from "./AssistanceCheckListComponent";
import AssistanceClientQuotations from "./AssistanceClientQuotations";
import { AssistanceDescriptionComponent } from './AssistanceDescriptionComponent';
import { AssistanceExecutionsComponent } from "./AssistanceExecutionsComponent";
import { AssistancePartsComponent } from "./AssistancePartsComponent";
import AssistanceRecallComponent from "./AssistanceRecallComponent";
import AssistanceServiceTripsComponent from "./AssistanceServiceTripsComponent";
import AssistanceWarrantyComponent from "./AssistanceWarrantyComponent";

type AssistanceNavBarType = {
    mode: "workshop" | "backoffice";
    assistance: AssistanceDTO;
    setAssistance: React.Dispatch<React.SetStateAction<AssistanceDTO>>;
    isLoadingAssistance: boolean;
    getAssistance: () => Promise<void>;
    reloadExecutions: boolean;
    childrenColumnSize?: number;
    onFetchExecutionsData: () => void;
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    childrenColumnSize: number;
}

function TabPanel({ children, value, index, childrenColumnSize, ...other }: TabPanelProps) {
    return (
        <Col xl={childrenColumnSize}>
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && <>{children}</>}
            </div>
        </Col>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

export function AssistanceNavBar({
    mode,
    assistance,
    setAssistance,
    isLoadingAssistance,
    getAssistance,
    reloadExecutions,
    childrenColumnSize = 12,
    ...props
}: AssistanceNavBarType) {
    const AssistanceDetails = useDynamicComponent("AssistanceDetails", {
        assistance,
        setAssistance,
        isLoadingAssistance,
        getAssistance,
    });

    const intl = useIntl();
    const isMobile = useMobile();
    const { availableModules } = useAvailableModulesContext();
    const [activeTab, setActiveTab] = useState<number>(0);
    const isComplete = !assistance.userCanEdit;
    const isAcceptedBudget = assistance.status?.toUpperCase() === "ORÇAMENTO ACEITE";

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const dynamicTabs = [
        ...(mode === "workshop"
            ? [{
                module: "Details",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Details" }),
                component: AssistanceDetails
            }]
            : []),

        {
            module: "Description",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Description" }),
            component: (
                <AssistanceDescriptionComponent
                    assistance={assistance}
                    setAssistance={setAssistance}
                    isLoadingAssistance={isLoadingAssistance}
                />
            )
        },

        {
            module: "Parts",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Parts" }),
            component: (
                <AssistancePartsComponent
                    assistanceId={assistance.id ?? 0}
                    reloadExecutions={reloadExecutions}
                    mode={mode}
                    isComplete={isComplete}
                    isAcceptedBudget={isAcceptedBudget}
                    assistanceStatus={assistance.status ?? ''}
                />
            )
        },

        {
            module: "Executions",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Executions" }),
            component: (
                <AssistanceExecutionsComponent
                    assistanceId={assistance.id ?? 0}
                    reloadExecutions={reloadExecutions}
                    mode={mode}
                    isComplete={isComplete}
                    onFetchExecutionsData={props.onFetchExecutionsData}
                />
            )
        },

        ...(availableModules.includes("IsActiveWarrantyModule")
            ? [{
                module: "Warranty",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Warranty" }),
                component: (
                    <AssistanceWarrantyComponent
                        assistance={assistance}
                        equipmentId={assistance.equipment?.id ?? 0}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveRecallModule")
            ? [{
                module: "Recall",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Recall" }),
                component: (
                    <AssistanceRecallComponent
                        assistance={assistance}
                        equipmentId={assistance.equipment?.id ?? 0}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),
        ...(availableModules.includes("IsActiveAssistanceCheckListModule")
            ? [{
                module: "CheckList",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.CheckList" }),
                component: (
                    <AssistanceCheckListComponent
                        assistance={assistance}
                        setAssistance={setAssistance}
                        loading={isLoadingAssistance}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveAditionalInfoModule")
            ? [{
                module: "AditionalInfo",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.AditionalInfo" }),
                component: (
                    <AssistanceAdditionalInfoComponent
                        assistance={assistance}
                        setAssistance={setAssistance}
                        isLoadingAssistance={isLoadingAssistance}
                    />
                )
            }]
            : []),

        {
            module: "Images",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Images" }),
            component: (
                <FilesList assistanceId={assistance.id ?? 0} reloadExecutions={reloadExecutions} mode={mode} />
            )
        },

        ...(availableModules.includes("ShowExternalAssistances")
            ? [{
                module: "Others",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Others" }),
                component: (
                    <AditionalInfo
                        assistanceId={assistance.id ?? 0}
                        reloadExecutions={reloadExecutions}
                        mode={mode}
                        assistance={assistance}
                        isComplete={isComplete}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("IsActiveTripsModule")
            ? [{
                module: "Trips",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Trips" }),
                component: (
                    <AssistanceServiceTripsComponent
                        assistanceId={assistance.id ?? 0}
                        totalDebtkmInAssistance={assistance.totalDebtKm}
                    />
                )
            }]
            : []),

        {
            module: "Status",
            label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Status" }),
            component: (
                <StatusHistoryTable assistanceId={assistance.id ?? 0} reloadExecutions={reloadExecutions} mode={mode} />
            )
        },

        ...(availableModules.includes("IsActiveClientQuotation")
            ? [{
                module: "Quotations",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Quotations" }),
                component: (
                    <AssistanceClientQuotations
                        assistanceStatus={assistance.status ?? ""}
                        assistanceId={assistance.id ?? 0}
                        clientId={assistance.client?.id ?? 0}
                        reloadSignal={reloadExecutions}
                        onReloadComplete={() => { }}
                    />
                )
            }]
            : []),

        ...(availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule") && mode === "backoffice"
            ? [{
                module: "Profitability",
                label: intl.formatMessage({ id: "Screens.Assistances.Navbar.Profitability" }),
                component: (
                    <AssistanceProfitabilityTable
                        assistanceId={assistance.id ?? 0}
                        reloadExecutions={reloadExecutions}
                        mode={mode}
                    />
                )
            }]
            : [])
    ];

    const selectItems: SelectItemsProps[] = dynamicTabs.map((tab, index) => ({
        value: index.toString(),
        label: tab.label
    }));

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                {isMobile ? (
                    <Row style={{ marginTop: "10px" }}>
                        <SelectComponent
                            xl={12}
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            label="Escolha uma aba"
                            items={selectItems}
                            onChange={(value) => setActiveTab(Number(value))}
                            selectedItemValue={activeTab.toString()}
                            style={{ width: "100%" }}
                        />
                    </Row>
                ) : (
                    <Tabs
                        value={activeTab}
                        onChange={handleChange}
                        aria-label="assistance tabs"
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                    >
                        {dynamicTabs.map((tab, index) => (
                            <Tab key={index} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                )}
            </Box>

            {dynamicTabs.map((tab, index) => (
                <TabPanel key={index} value={activeTab} index={index} childrenColumnSize={childrenColumnSize}>
                    {tab.component}
                </TabPanel>
            ))}
        </>
    );
}
